import settings from '@/constants/constants';
import { Logger } from '@/utils/logger';
import { DELIVERY_OPTION, DELIVERY_OPTIONS } from '@montugroup/circuit-shared';
import { Alert, AlertTitle, Link, Typography, ViewObserver } from '@montugroup/design-system';
import { useEffect } from 'react';

const logger = new Logger('OrderHistoryValidIdAlert');

const HELP_LINKS: Partial<Record<(typeof DELIVERY_OPTIONS)[DELIVERY_OPTION]['name'], string>> = {
  [DELIVERY_OPTIONS[DELIVERY_OPTION.SameDayAfternoon].name]:
    `${settings.support.site.url}/articles/14664095252751-Receiving-your-This-afternoon-delivery`,
  [DELIVERY_OPTIONS[DELIVERY_OPTION.ClickAndCollect].name]:
    `${settings.support.site.url}/articles/14108814616463-Identification-required-to-receive-your-medication`,
};

export const OrderHistoryValidIdAlert = ({
  deliveryOptionName,
}: {
  deliveryOptionName: (typeof DELIVERY_OPTIONS)[DELIVERY_OPTION]['name'];
}) => {
  const helpLink = HELP_LINKS[deliveryOptionName];

  const isClickAndCollectOrder = deliveryOptionName === 'ClickAndCollect';

  const title = isClickAndCollectOrder ? (
    <>Bring ID when your order is ready</>
  ) : (
    <>Make sure you&apos;ve got valid ID</>
  );

  const description = isClickAndCollectOrder ? (
    <>
      Keep an eye on your email and SMS for updates on your order. Bring&nbsp;
      <Link id="shipping_valid_id_alert_help_link" href={helpLink} target="_blank" rel="noopener noreferrer">
        valid ID
      </Link>
      &nbsp;when it’s ready to collect.
    </>
  ) : (
    <>
      To receive your order, you must be present with valid ID.
      {helpLink && (
        <Link id="shipping_valid_id_alert_help_link" href={helpLink} target="_blank" rel="noopener noreferrer">
          Learn more.
        </Link>
      )}
    </>
  );

  useEffect(() => {
    if (!helpLink) {
      logger.critical('missing helpLink', {
        deliveryOptionName,
      });
    }
  }, [deliveryOptionName, helpLink]);

  return (
    <Alert severity="info" sx={{ width: '100%' }} data-dd-privacy="allow">
      <ViewObserver id={'order-history-valid-id-alert'}>
        <AlertTitle sx={{ fontWeight: 'bold' }}>{title}</AlertTitle>
        <Typography variant="body2">{description}</Typography>
      </ViewObserver>
    </Alert>
  );
};
